import { useState } from "react";
import { FileDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TACTICAL_DESCRIPTIONS } from "@/lib/tacticalDescriptions";

interface ForensicReportExportProps {
  message: string;
  patternLabel: string;
  manipulationScore: number;
  toxicityScore: number;
  confidence: "high" | "moderate" | "low";
  signals: string[];
  shortExplanation: string;
  nuance: string;
  savedResponse?: string | null;
  savedTimestamp?: string | null;
  hasScript?: boolean;
  hasExported?: boolean;
  primaryPattern: string;
}

const ForensicReportExport = ({
  message,
  patternLabel,
  manipulationScore,
  toxicityScore,
  confidence,
  signals,
  shortExplanation,
  nuance,
  savedResponse,
  savedTimestamp,
  hasScript,
  hasExported,
}: ForensicReportExportProps) => {
  const [generating, setGenerating] = useState(false);

  const severityLabel = (v: number, type: string) => {
    if (v >= 7) return "CRITICAL";
    if (v >= 5.5) return type === "Manipulation" ? "AMBIGUOUS / MODERATE" : "MODERATE";
    if (v >= 4) return "LOW-MODERATE";
    return "LOW";
  };
  const severityRGB = (v: number): [number, number, number] =>
    v >= 7 ? [239, 68, 68] : v >= 5.5 ? [245, 158, 11] : v >= 4 ? [180, 160, 100] : [100, 116, 139];

  const handleDownload = async () => {
    setGenerating(true);
    try {
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const margin = 48;
      const contentW = W - margin * 2;
      let y = 0;

      // ── Background
      doc.setFillColor(12, 12, 12);
      doc.rect(0, 0, W, H, "F");

      // ── Top accent line
      doc.setFillColor(0, 242, 255);
      doc.rect(0, 0, W, 3, "F");

      y = 40;

      // ── Header
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0, 242, 255);
      doc.text("GREYROCK AI", margin, y);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text("FORENSIC REPORT", W - margin, y, { align: "right" });

      y += 16;
      doc.setDrawColor(40, 40, 40);
      doc.line(margin, y, W - margin, y);

      y += 20;
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      const reportDate = savedTimestamp
        ? new Date(savedTimestamp).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
        : new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
      doc.text(`Generated: ${reportDate}`, margin, y);
      doc.text("CONFIDENTIAL", W - margin, y, { align: "right" });

      // ── Section helper
      const sectionHeader = (label: string, yPos: number) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(0, 242, 255);
        doc.text(label, margin, yPos);
        yPos += 4;
        doc.setDrawColor(0, 242, 255);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, margin + doc.getTextWidth(label), yPos);
        doc.setLineWidth(1);
        return yPos + 12;
      };

      const bodyText = (text: string, yPos: number, maxW?: number) => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(210, 210, 210);
        const lines = doc.splitTextToSize(text, maxW || contentW);
        doc.text(lines, margin, yPos);
        return yPos + lines.length * 14 + 4;
      };

      const checkPage = (needed: number, currentY: number) => {
        if (currentY + needed > H - 60) {
          doc.addPage();
          doc.setFillColor(12, 12, 12);
          doc.rect(0, 0, W, H, "F");
          return 48;
        }
        return currentY;
      };

      // ══════════════════════════════════════════
      // SUMMARY BLOCK
      // ══════════════════════════════════════════
      y += 28;
      doc.setFillColor(18, 18, 18);
      doc.setDrawColor(0, 242, 255);
      doc.setLineWidth(0.75);
      const summaryH = 52;
      doc.roundedRect(margin, y - 4, contentW, summaryH, 6, 6, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(240, 240, 240);
      doc.text(patternLabel.toUpperCase(), margin + 16, y + 18);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(160, 160, 160);
      const summaryLine = `${manipulationScore.toFixed(1)} Manipulation  |  ${toxicityScore.toFixed(1)} Emotional Harm`;
      doc.text(summaryLine, margin + 16, y + 36);
      doc.setLineWidth(1);
      y += summaryH + 20;

      // ══════════════════════════════════════════
      // 1. ANALYZED PHRASE
      // ══════════════════════════════════════════
      y = sectionHeader("ANALYZED PHRASE", y);
      doc.setFillColor(26, 26, 26);
      const phraseLines = doc.splitTextToSize(`"${message}"`, contentW - 28);
      const phraseH = phraseLines.length * 14 + 20;
      doc.roundedRect(margin, y - 4, contentW, phraseH, 4, 4, "F");
      doc.setFillColor(0, 242, 255);
      doc.rect(margin, y - 4, 3, phraseH, "F");
      doc.setFont("helvetica", "italic");
      doc.setFontSize(10);
      doc.setTextColor(200, 200, 200);
      doc.text(phraseLines, margin + 16, y + 12);
      y += phraseH + 20;

      // (Pattern already shown in summary block)

      // ══════════════════════════════════════════
      // 3. SCORES
      // ══════════════════════════════════════════
      y = checkPage(100, y);
      y = sectionHeader("THREAT ASSESSMENT", y);
      y += 4;

      const scoreBoxW = (contentW - 16) / 3;
      const scores = [
        { label: "Manipulation", value: manipulationScore },
        { label: "Emotional Harm", value: toxicityScore },
        { label: "Confidence", value: confidence === "high" ? 0.9 : confidence === "moderate" ? 0.65 : 0.4 },
      ];

      scores.forEach((s, i) => {
        const bx = margin + i * (scoreBoxW + 8);
        doc.setFillColor(22, 22, 22);
        doc.setDrawColor(36, 36, 36);
        doc.roundedRect(bx, y, scoreBoxW, 62, 5, 5, "FD");

        // Score value
        const isConf = s.label === "Confidence";
        const c: [number, number, number] = isConf ? [0, 242, 255] : severityRGB(s.value);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(24);
        doc.setTextColor(c[0], c[1], c[2]);
        doc.text(
          isConf ? `${Math.round(s.value * 100)}%` : s.value.toFixed(1),
          bx + scoreBoxW / 2,
          y + 26,
          { align: "center" }
        );

        // Label
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(130, 130, 130);
        doc.text(s.label.toUpperCase(), bx + scoreBoxW / 2, y + 42, { align: "center" });

        // Severity band label
        const bandLabel = isConf
          ? (s.value >= 0.8 ? "HIGH" : s.value >= 0.55 ? "MODERATE" : "LOW")
          : severityLabel(s.value, s.label);
        const bandC: [number, number, number] = isConf ? [0, 200, 220] : c;
        doc.setFontSize(6);
        doc.setTextColor(bandC[0], bandC[1], bandC[2]);
        doc.text(bandLabel, bx + scoreBoxW / 2, y + 54, { align: "center" });
      });
      y += 80;

      // ══════════════════════════════════════════
      // 4. SIGNALS DETECTED
      // ══════════════════════════════════════════
      if (signals.length > 0) {
        y = checkPage(50, y);
        y = sectionHeader("SIGNALS DETECTED", y);
        y += 2;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.5);
        doc.setTextColor(0, 220, 240);
        const signalStr = signals.join("  \u2022  ");
        const signalLines = doc.splitTextToSize(signalStr, contentW);
        doc.text(signalLines, margin, y);
        y += signalLines.length * 14 + 16;
      }

      // ══════════════════════════════════════════
      // 5. UNDERLYING TACTIC
      // ══════════════════════════════════════════
      y = checkPage(70, y);
      y = sectionHeader("UNDERLYING TACTIC", y);
      y += 2;
      // Use strongest available tactic copy from tactical descriptions
      const tacticKey = patternLabel.toUpperCase();
      const tacticEntry = TACTICAL_DESCRIPTIONS[tacticKey];
      const tacticText = tacticEntry?.description || shortExplanation;
      y = bodyText(tacticText, y);
      y += 12;

      // ══════════════════════════════════════════
      // 6. NUANCE ASSESSMENT
      // ══════════════════════════════════════════
      y = checkPage(70, y);
      y = sectionHeader("NUANCE ASSESSMENT", y);
      y += 2;
      y = bodyText(nuance, y);
      y += 12;

      // ══════════════════════════════════════════
      // 7. NEUTRALIZED RESPONSE
      // ══════════════════════════════════════════
      if (savedResponse) {
        y = checkPage(80, y);
        y = sectionHeader("NEUTRALIZED RESPONSE", y);
        doc.setFillColor(20, 28, 20);
        const respLines = doc.splitTextToSize(savedResponse, contentW - 20);
        const respH = respLines.length * 14 + 16;
        y = checkPage(respH + 10, y);
        doc.roundedRect(margin, y - 4, contentW, respH, 4, 4, "F");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(160, 220, 160);
        doc.text(respLines, margin + 10, y + 10);
        y += respH + 16;
      }

      // ══════════════════════════════════════════
      // 8. SAVED CONTENT STATUS
      // ══════════════════════════════════════════
      const assets: string[] = [];
      if (savedResponse) assets.push("Response Saved");
      if (hasScript) assets.push("Script Generated");
      if (hasExported) assets.push("Card Exported");

      if (assets.length > 0) {
        y = checkPage(40, y);
        y = sectionHeader("SAVED ASSETS", y);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(160, 160, 160);
        assets.forEach((a) => {
          doc.text("- " + a, margin + 4, y);
          y += 18;
        });
        y += 8;
      }

      // ══════════════════════════════════════════
      // FOOTER
      // ══════════════════════════════════════════
      const footerY = H - 40;
      doc.setDrawColor(40, 40, 40);
      doc.line(margin, footerY - 12, W - margin, footerY - 12);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(80, 80, 80);
      doc.text("GreyRockAI.app", margin, footerY);
      doc.text(`© ${new Date().getFullYear()} GreyRock AI`, W - margin, footerY, { align: "right" });

      const disclaimerY = footerY + 12;
      doc.setFontSize(6);
      doc.setTextColor(60, 60, 60);
      const disclaimer = "This report is an AI-assisted communication pattern analysis and should be interpreted in context. It does not constitute clinical diagnosis or legal advice.";
      const discLines = doc.splitTextToSize(disclaimer, contentW);
      doc.text(discLines, margin, disclaimerY);

      // ── Save
      const dateSlug = (savedTimestamp ? new Date(savedTimestamp) : new Date())
        .toISOString().slice(0, 10);
      const patternSlug = patternLabel.replace(/\s+/g, "_");
      doc.save(`GreyRockAI_Forensic_Report_${patternSlug}_${dateSlug}.pdf`);

      toast.success("Forensic report downloaded");
    } catch (err) {
      console.error("PDF generation failed:", err);
      toast.error("Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Button
      onClick={handleDownload}
      disabled={generating}
      variant="ghost"
      className="h-12 px-5 flex items-center justify-center gap-2 bg-muted/40 border border-border hover:border-cyan/40 hover:bg-cyan/5 text-muted-foreground hover:text-cyan rounded-xl transition-all"
    >
      {generating ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <FileDown className="w-4 h-4" />
      )}
      <span className="text-xs font-semibold uppercase tracking-wider">
        {generating ? "Generating…" : "Report"}
      </span>
    </Button>
  );
};

export default ForensicReportExport;
